import React, { useState, useEffect } from 'react';

const API       = 'https://v-room.app/api';
const LS_KEY    = 'vroom_beta_email';
const LS_STATUS = 'vroom_beta_status';   // 'confirmed' | 'pending_check'
const LS_TS     = 'vroom_beta_ts';       // timestamp ostatniego potwierdzenia z API
const TTL_MS    = 1000 * 60 * 60 * 24;  // 24h — po tym czasie zawsze re-sprawdza API

// ── helpers ────────────────────────────────────────────────
function readCache() {
  const email  = localStorage.getItem(LS_KEY);
  const status = localStorage.getItem(LS_STATUS);
  const ts     = parseInt(localStorage.getItem(LS_TS) || '0', 10);
  return { email, status, ts };
}

function writeCache(email) {
  localStorage.setItem(LS_KEY,    email);
  localStorage.setItem(LS_STATUS, 'confirmed');
  localStorage.setItem(LS_TS,     Date.now().toString());
}

function clearCache() {
  localStorage.removeItem(LS_KEY);
  localStorage.removeItem(LS_STATUS);
  localStorage.removeItem(LS_TS);
}

function isCacheFresh(ts) {
  return Date.now() - ts < TTL_MS;
}

// ── component ──────────────────────────────────────────────
export default function BetaSignupSection() {
  const [email,    setEmail]    = useState('');
  const [name,     setName]     = useState('');
  const [platform, setPlatform] = useState('both');
  const [loading,  setLoading]  = useState(false);
  const [checking, setChecking] = useState(false);
  const [msg,      setMsg]      = useState(null);

  // ── 1. mount — sprawdź cache + API ───────────────────────
  useEffect(() => {
    const { email: cachedEmail, status, ts } = readCache();

    if (!cachedEmail) return; // brak cache — pokaż formularz od razu

    if (status === 'confirmed' && isCacheFresh(ts)) {
      // cache świeży (< 24h) — pokaż "już zapisany" bez API
      setMsg({ type: 'already', text: `✅ Już jesteś zapisany na listę beta! (${cachedEmail})` });
      return;
    }

    // cache stary (> 24h) lub status != confirmed — zweryfikuj z API
    verifyWithApi(cachedEmail);
  }, []);

  // ── 2. weryfikacja z API ──────────────────────────────────
  const verifyWithApi = async (emailToCheck) => {
    setChecking(true);
    try {
      const res  = await fetch(`${API}/beta/check`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email: emailToCheck }),
      });
      const data = await res.json();

      if (res.ok && data.registered) {
        // nadal w bazie — odśwież cache
        writeCache(emailToCheck);
        setMsg({ type: 'already', text: `✅ Już jesteś zapisany na listę beta! (${emailToCheck})` });
      } else {
        // admin usunął lub błąd — wyczyść cache, pokaż formularz
        clearCache();
        setMsg(null);
      }
    } catch {
      // błąd sieci — nie karaj użytkownika, pokaż formularz
      clearCache();
      setMsg(null);
    } finally {
      setChecking(false);
    }
  };

  // ── 3. onBlur emaila — sprawdź czy już zapisany ──────────
  const handleEmailBlur = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;

    const { email: cachedEmail, status, ts } = readCache();

    // cache świeży dla tego samego emaila — nie odpytuj API
    if (cachedEmail === email && status === 'confirmed' && isCacheFresh(ts)) {
      setMsg({ type: 'already', text: `✅ Już jesteś zapisany na listę beta! (${email})` });
      return;
    }

    // inny email lub stary cache — zapytaj API
    setChecking(true);
    try {
      const res  = await fetch(`${API}/beta/check`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email }),
      });
      const data = await res.json();

      if (res.ok && data.registered) {
        writeCache(email);
        setMsg({ type: 'already', text: `✅ Już jesteś zapisany na listę beta! (${email})` });
      } else {
        // nie ma w bazie — wyczyść ewentualny stary cache, zostaw formularz
        if (cachedEmail === email) clearCache();
        setMsg(null);
      }
    } catch {
      // cicho — nie blokuj formularza
    } finally {
      setChecking(false);
    }
  };

  // ── 4. submit ─────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setMsg({ type: 'error', text: 'Podaj poprawny adres email' });
      return;
    }
    setLoading(true);
    setMsg(null);
    try {
      const res  = await fetch(`${API}/beta/signup`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email, name, platform }),
      });
      const data = await res.json();

      if (res.ok) {
        writeCache(email);
        setMsg({ type: 'success', text: '🎉 Jesteś na liście! Odezwiemy się wkrótce.' });
        setEmail(''); setName(''); setPlatform('both');
      } else if (res.status === 409) {
        writeCache(email); // już był — zapamiętaj
        setMsg({ type: 'already', text: `✅ Już jesteś zapisany na listę beta! (${email})` });
      } else {
        setMsg({ type: 'error', text: data.error || 'Coś poszło nie tak' });
      }
    } catch {
      setMsg({ type: 'error', text: 'Błąd połączenia z serwerem' });
    } finally {
      setLoading(false);
    }
  };

  // ── render ────────────────────────────────────────────────
  return (
    <section className="beta-section">
      <div className="beta-inner">
        <span className="beta-badge">🔒 ZAMKNIĘTA BETA</span>
        <h2 className="beta-heading">
          <span>🚀</span> DOŁĄCZ DO BETA
        </h2>
        <p className="beta-sub">
          Testuj VROOM jako pierwszy. Ekskluzywny wczesny dostęp.
        </p>

        {/* Spinner gdy weryfikujemy przy wejściu na stronę */}
        {checking && !email && (
          <div className="beta-verifying">
            <div className="btn-spinner"></div>
            Weryfikowanie...
          </div>
        )}

        {!checking && (msg?.type === 'success' || msg?.type === 'already') ? (
          <div className={`beta-success ${msg.type === 'already' ? 'beta-already' : ''}`}>
            <span className="material-icons">
              {msg.type === 'already' ? 'how_to_reg' : 'check_circle'}
            </span>
            <div>
              <div>{msg.text}</div>
              {msg.type === 'already' && (
                <div className="beta-already-sub">Damy znać gdy tylko wystartuje! 🚀</div>
              )}
              {/* Przycisk "to nie ja / inny email" */}
              <button
                type="button"
                className="beta-not-me-btn"
                onClick={() => { clearCache(); setMsg(null); setEmail(''); }}
              >
                To nie mój email / zapisz inny
              </button>
            </div>
          </div>
        ) : (
          !checking && (
            <form className="beta-form" onSubmit={handleSubmit}>
              <div className={`beta-input-wrap ${checking ? 'beta-input-checking' : ''}`}>
                <span className="material-icons">email</span>
                <input
                  type="email"
                  placeholder="Twój adres email *"
                  value={email}
                  onChange={e => { setEmail(e.target.value); setMsg(null); }}
                  onBlur={handleEmailBlur}
                  required
                />
                {checking && <div className="beta-check-spinner"></div>}
              </div>

              <div className="beta-input-wrap">
                <span className="material-icons">person</span>
                <input
                  type="text"
                  placeholder="Imię (opcjonalnie)"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  maxLength={50}
                />
              </div>

              <div className="beta-platform-selector">
                {['android', 'ios', 'both'].map(p => (
                  <button
                    key={p}
                    type="button"
                    className={`beta-platform-btn ${platform === p ? 'active' : ''}`}
                    onClick={() => setPlatform(p)}
                  >
                    {p === 'android' ? '🤖 Android'
                      : p === 'ios' ? ' iOS'
                      : '📱 Oba'}
                  </button>
                ))}
              </div>

              {msg?.type === 'error' && (
                <div className="beta-error">{msg.text}</div>
              )}

              <button type="submit" className="btn-beta-submit" disabled={loading || checking}>
                {loading
                  ? <><div className="btn-spinner"></div>Zapisywanie...</>
                  : <><span className="material-icons">rocket_launch</span>DOŁĄCZ DO BETA</>
                }
              </button>
            </form>
          )
        )}
      </div>
    </section>
  );
}